import { createSelector } from "@reduxjs/toolkit";

/* The HousingProductLocationContentCommand class is a constructor function that returns a selector
function used to create a canonical URL and search location command based on input parameters. */
export default class HousingProductLocationContentCommand {

/**
 * This is a constructor function that takes in three parameters.
 * @param selectHousingProductLocationContentContext - This parameter is likely a context or reference
 * to the HTML element that contains the housing product location content. It could be used to
 * manipulate or retrieve information from that element using JavaScript or other web technologies.
 * @param urlQueryStringSelector - It is a string that represents a selector used to extract the query
 * string from the URL.
 * @param selectSearchCommandContext - It is a context object that is used to select a search command.
 * It is likely used in a larger codebase to handle user input and trigger search functionality.
 */
    constructor(
        selectHousingProductLocationContentContext,
        urlQueryStringSelector, 
        selectSearchCommandContext,
        selectConstructQueryString) {
        this.selectHousingProductLocationContentContext = selectHousingProductLocationContentContext;
        this.urlQueryStringSelector = urlQueryStringSelector;
        this.selectSearchCommandContext = selectSearchCommandContext;
        this.selectConstructQueryString = selectConstructQueryString;
    }

/**
 * The function returns a selector that creates a canonical URL and a search location command based on
 * various input parameters.
 * @returns A selector function is being returned that takes in three arguments:
 * `selectHousingProductLocationContentContext`, `urlQueryStringSelector`, and
 * `selectSearchCommandContext`. The function returns an object with two properties:
 * `createCanonicalUrlCommand` and `searchLocationCommand`. The values of these properties are objects
 * that are created by merging the values of the input arguments.
 */
    selectHousingProduct() {
        return createSelector(
            this.selectHousingProductLocationContentContext,
            this.urlQueryStringSelector,
            this.selectSearchCommandContext,
            this.selectConstructQueryString,
            (locationContentCommand, queryStringSelector, searchParams, queryString) => {
            const parameters = new URLSearchParams(queryString);
            const queryStrings = Array.from(parameters.keys());
            const searchCommand =  structuredClone(searchParams);
            searchCommand.searchParameters.queryStrings =  queryStrings;
            return {
              createCanonicalUrlCommand: { ...locationContentCommand, ...queryStringSelector },
              searchLocationCommand: { ...searchCommand },
            }});
    } 
}